import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import StatusPill from "@/components/public/StatusPill";
import BrandFooter from "@/components/BrandFooter";
import { HERO_CTA } from "@/data/marketing_content";

/**
 * Shared marketing shell — every /product, /features/:slug, /together/*,
 * /calendar, /inbox, /health, /intelligence, /about page renders through
 * this component so copy stays consistent and every SEO tag is set.
 */
export default function MarketingPage({
    slug,               // used for data-testid
    title,              // <title> + og:title
    description,        // meta description + og:description
    canonical,          // canonical URL path (leading /)
    hero, sub,
    statusKey,          // optional Live/Beta/Coming Soon pill
    bullets = [],
    tabs = [],          // optional pill row (e.g. Mine/Partner/Joint)
    disclaimer,
    primaryCta = HERO_CTA.primary,
    secondaryCta = HERO_CTA.secondary,
    children,           // optional extra sections
}) {
    const canonicalUrl = canonical ? `https://vaultbypd.com${canonical}` : undefined;
    const fullTitle = title ? String(title) + " · VAULT by PD" : "VAULT by PD";
    return (
        <div className="min-h-screen bg-stone-50" data-testid={`marketing-${slug}`}>
            <Helmet>
                <title>{fullTitle}</title>
                {description && <meta name="description" content={description} />}
                <meta property="og:title" content={fullTitle} />
                {description && <meta property="og:description" content={description} />}
                {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="VAULT by PD" />
            </Helmet>

            {/* Public top nav */}
            <header className="border-b border-stone-200 bg-white sticky top-0 z-40" data-testid="public-nav">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
                    <Link to="/" className="font-display text-xl text-stone-900" data-testid="public-nav-logo">VAULT · by PD</Link>
                    <nav className="hidden md:flex items-center gap-4 text-[13px] text-stone-600 ml-4">
                        <Link to="/product" className="hover:text-stone-900" data-testid="nav-product">Product</Link>
                        <Link to="/together" className="hover:text-stone-900" data-testid="nav-together">Together</Link>
                        <Link to="/pricing" className="hover:text-stone-900" data-testid="nav-pricing">Pricing</Link>
                        <Link to="/demo" className="hover:text-stone-900" data-testid="nav-demo">Demo</Link>
                        <Link to="/trust" className="hover:text-stone-900" data-testid="nav-trust">Trust</Link>
                        <Link to="/resources" className="hover:text-stone-900" data-testid="nav-resources">Resources</Link>
                    </nav>
                    <div className="ml-auto flex items-center gap-2">
                        <Link to="/login" className="text-[13px] font-semibold text-stone-700 hover:text-stone-900 hidden sm:inline" data-testid="nav-login">Sign in</Link>
                        <Link to={primaryCta.to} className="text-[12.5px] font-semibold text-white bg-stone-900 rounded-full px-3.5 py-1.5 hover:bg-stone-700" data-testid="nav-cta">{primaryCta.label}</Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-4xl mx-auto px-6 pt-14 pb-10 text-left">
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-[10.5px] font-bold uppercase tracking-widest text-amber-800">VAULT by PD</span>
                    {statusKey && <StatusPill featureKey={statusKey} />}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05]" data-testid="marketing-hero">
                    {hero}
                </h1>
                {sub && <p className="text-base sm:text-lg text-stone-600 mt-5 max-w-2xl leading-relaxed">{sub}</p>}
                <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link to={primaryCta.to}
                          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-white bg-stone-900 rounded-full px-5 py-2.5 hover:bg-stone-700"
                          data-testid={primaryCta.testid}>
                        {primaryCta.label} <ArrowRight size={13} />
                    </Link>
                    {secondaryCta && (
                        <Link to={secondaryCta.to}
                              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-stone-700 border border-stone-300 rounded-full px-5 py-2.5 hover:border-stone-500"
                              data-testid={secondaryCta.testid}>
                            {secondaryCta.label}
                        </Link>
                    )}
                </div>
                {tabs.length > 0 && (
                    <div className="mt-8 inline-flex gap-1 border border-stone-200 rounded-full p-1 bg-white" data-testid="marketing-tabs">
                        {tabs.map(t => (
                            <span key={t}
                                  className="px-4 py-1.5 text-[12.5px] font-semibold rounded-full text-stone-600">
                                {t}
                            </span>
                        ))}
                    </div>
                )}
            </section>

            {/* Bullets */}
            {bullets.length > 0 && (
                <section className="max-w-4xl mx-auto px-6 pb-10" data-testid="marketing-bullets">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                        {bullets.map(b => (
                            <div key={b} className="flex items-start gap-2 rounded-xl border border-stone-200 bg-white p-3.5">
                                <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                                <span className="text-[13.5px] text-stone-800 leading-snug">{b}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {children}

            {/* Disclaimer */}
            {disclaimer && (
                <section className="max-w-4xl mx-auto px-6 pb-10" data-testid="marketing-disclaimer">
                    <div className="border-l-2 border-amber-300 bg-amber-50/40 px-4 py-3 text-[12px] text-stone-700 flex items-start gap-2">
                        <ShieldCheck size={13} className="mt-0.5 text-amber-800 shrink-0" />
                        {disclaimer}
                    </div>
                </section>
            )}

            {/* Bottom CTA */}
            <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="marketing-bottom-cta">
                <div className="rounded-2xl border border-stone-200 bg-stone-900 text-white px-6 py-8 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-400">Ready?</div>
                        <div className="text-xl font-display mt-1">Bring your financial life into VAULT.</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to={HERO_CTA.primary.to} className="text-[13px] font-semibold bg-white text-stone-900 rounded-full px-4 py-2 hover:bg-amber-100">
                            {HERO_CTA.primary.label}
                        </Link>
                        <Link to={HERO_CTA.secondary.to} className="text-[13px] font-semibold text-white border border-white/30 rounded-full px-4 py-2 hover:bg-white/10">
                            {HERO_CTA.secondary.label}
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-stone-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-6">
                    <BrandFooter />
                    <div className="text-[11px] text-stone-500 mt-3">
                        © {new Date().getFullYear()} VAULT by PD · Financial life, organised. VAULT is not a bank, lender, or investment adviser.
                    </div>
                </div>
            </footer>
        </div>
    );
}
